import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Calculator, BadgeCheck, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { DESTINATIONS, VISA_SERVICES } from "@/lib/siteData";

const CURRENCIES = ["USD", "AED", "ZAR", "EUR", "GBP", "KES", "NGN", "BWP", "ZMW"];
const inputCls = "w-full rounded-lg border border-[#E5E7EB] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5 block";

const CurrencyConverter = () => {
  const [rates, setRates] = useState(null);
  const [amount, setAmount] = useState("1000");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("AED");
  useEffect(() => {
    fetch("https://open.er-api.com/v6/latest/USD").then((r) => r.json()).then((d) => setRates(d.rates)).catch(() => setRates(null));
  }, []);
  const result = rates && rates[from] && rates[to] ? ((parseFloat(amount) || 0) / rates[from]) * rates[to] : null;
  return (
    <div data-testid="currency-converter">
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={labelCls}>Amount</label><input value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="cc-amount-input" /></div>
        <div><label className={labelCls}>From</label><select value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="cc-from-select">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div><label className={labelCls}>To</label><select value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="cc-to-select">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></div>
      </div>
      <div className="mt-6 bg-[#0A2E4D] rounded-xl p-6 text-center">
        {result != null ? (
          <>
            <div className="font-serif-display text-3xl text-[#E3BA45]" data-testid="cc-result">{result.toLocaleString(undefined, { maximumFractionDigits: 2 })} {to}</div>
            <div className="text-white/60 text-xs mt-2">Live mid-market rate · 1 {from} = {(rates[to] / rates[from]).toFixed(4)} {to}</div>
          </>
        ) : <div className="text-white/70 text-sm">Loading live rates…</div>}
      </div>
    </div>
  );
};

const CostCalculator = () => {
  const [dest, setDest] = useState("Dubai");
  const [nights, setNights] = useState("4");
  const [travellers, setTravellers] = useState("2");
  const [tier, setTier] = useState("1");
  const d = DESTINATIONS.find((x) => x.name === dest);
  const total = d ? Math.round((d.price + (parseInt(nights) || 0) * 85 * parseFloat(tier)) * (parseInt(travellers) || 1)) : 0;
  return (
    <div data-testid="cost-calculator">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div><label className={labelCls}>Destination</label><select value={dest} onChange={(e) => setDest(e.target.value)} className={inputCls} data-testid="calc-dest-select">{DESTINATIONS.map((x) => <option key={x.name}>{x.name}</option>)}</select></div>
        <div><label className={labelCls}>Nights</label><input type="number" min="1" value={nights} onChange={(e) => setNights(e.target.value)} className={inputCls} data-testid="calc-nights-input" /></div>
        <div><label className={labelCls}>Travellers</label><input type="number" min="1" value={travellers} onChange={(e) => setTravellers(e.target.value)} className={inputCls} data-testid="calc-travellers-input" /></div>
        <div><label className={labelCls}>Hotel Tier</label><select value={tier} onChange={(e) => setTier(e.target.value)} className={inputCls}><option value="0.7">3★ Comfort</option><option value="1">4★ Premium</option><option value="1.6">5★ Luxury</option></select></div>
      </div>
      <div className="mt-6 bg-[#0A2E4D] rounded-xl p-6 text-center">
        <div className="text-white/60 text-xs uppercase tracking-[0.2em] mb-1">Estimated Trip Cost (flights + hotel)</div>
        <div className="font-serif-display text-3xl text-[#E3BA45]" data-testid="calc-result">from ${total.toLocaleString()} USD</div>
        <Link to="/holidays/request" className="inline-flex items-center gap-2 text-white text-sm mt-3 border-b border-[#D4A62A] pb-0.5 hover:gap-3 transition-all">Get an exact quote <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
};

const VisaChecker = () => {
  const [nationality, setNationality] = useState("Zimbabwe");
  const [dest, setDest] = useState("Dubai / UAE");
  const [checked, setChecked] = useState(false);
  const v = VISA_SERVICES.find((x) => x.country === dest);
  return (
    <div data-testid="visa-checker">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Your Nationality</label>
          <select value={nationality} onChange={(e) => { setNationality(e.target.value); setChecked(false); }} className={inputCls} data-testid="vc-nationality-select">
            {["Zimbabwe", "South Africa", "Nigeria", "Kenya", "Ghana", "Zambia", "United Arab Emirates", "India", "Pakistan", "Other"].map((n) => <option key={n}>{n}</option>)}
          </select></div>
        <div><label className={labelCls}>Destination</label>
          <select value={dest} onChange={(e) => { setDest(e.target.value); setChecked(false); }} className={inputCls} data-testid="vc-dest-select">
            {VISA_SERVICES.map((x) => <option key={x.country}>{x.country}</option>)}
          </select></div>
      </div>
      <button onClick={() => setChecked(true)} className="mk-btn-gold mt-5 w-full sm:w-auto" data-testid="vc-check-btn"><BadgeCheck size={16} /> Check Eligibility</button>
      {checked && v && (
        <div className="mt-6 bg-[#0A2E4D] rounded-xl p-6" data-testid="vc-result">
          <div className="font-serif-display text-xl text-[#E3BA45]">Good news — we can assist with your {v.country} visa</div>
          <p className="text-white/80 text-sm mt-2 leading-relaxed">As a {nationality} passport holder, you can apply through Makchester. Typical processing: <span className="text-[#E3BA45] font-semibold">{v.processing}</span> · Validity: <span className="text-[#E3BA45] font-semibold">{v.validity}</span>.</p>
          <Link to="/visa" className="inline-flex items-center gap-2 text-white text-sm mt-4 border-b border-[#D4A62A] pb-0.5 hover:gap-3 transition-all">Start your application <ArrowRight size={14} /></Link>
        </div>
      )}
    </div>
  );
};

const TOOLS = [
  { id: "currency", label: "Currency Converter", icon: RefreshCw },
  { id: "calculator", label: "Travel Cost Calculator", icon: Calculator },
  { id: "visa", label: "Visa Eligibility Checker", icon: BadgeCheck },
];

export const TravelTools = () => {
  const [tool, setTool] = useState("currency");
  return (
    <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="travel-tools-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Travel Tools" title="Plan smarter, travel better" subtitle="Free tools powered by live data to help you budget your next trip." />
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {TOOLS.map((t) => (
            <button key={t.id} onClick={() => setTool(t.id)} data-testid={`tool-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${tool === t.id ? "bg-[#0A2E4D] text-white" : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#D4A62A]"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {tool === "currency" && <CurrencyConverter />}
          {tool === "calculator" && <CostCalculator />}
          {tool === "visa" && <VisaChecker />}
          <p className="mt-6 text-[11px] text-[#9CA3AF] leading-relaxed" data-testid="tools-disclaimer">
            {tool === "currency" && "Disclaimer: rates shown are live mid-market indications and may differ from the final rate applied by banks or payment providers."}
            {tool === "calculator" && "Disclaimer: estimates are indicative only, based on average seasonal pricing. Your consultant will confirm an exact, personalised quote."}
            {tool === "visa" && "Disclaimer: eligibility results are a general guide only. Final visa approval rests solely with the relevant immigration authorities."}
          </p>
        </div>
      </div>
    </section>
  );
};
